import {
  Button,
  Divider,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Selection,
  Spacer,
  Switch,
} from '@nextui-org/react';
import SettingsToggle from './SettingsToggle';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  selectActorConfig,
  selectAssistantConfig,
  selectSettingsDeveloper,
  selectSettingsFeatures,
  setActorModel,
  setAssistantModel,
  setProposeResponseModel,
  setAutomaticReplies,
  setActorDetails,
  setActorProfile,
  setSettingsAnalytics,
  setSettingsDeveloper,
  setTypingSimulated,
  selectSettingsAnalytics,
} from '../../store/settings/settingsSlice';
import { SettingsFeature } from '../../types';
import { PropsWithChildren, useMemo } from 'react';
import { FaAngleDown } from 'react-icons/fa6';
import Icon from '../base/Icon';
import SettingsRow from './SettingsRow';
import PromptButton from '../prompt/PromptButton';
import { logout } from '../../store/auth/authThunks';
import ButtonWithConfirmModal from '../base/ButtonWithConfirmModal';
import { GENERIC_APP } from '../../config/generic';
import { SOS_APP } from '../../config/sos';
import { selectUserRole } from '../../store/auth/authSlice';
import { getModels } from '../../util';

export interface SettingsModalProps {
  isOpen: boolean;
  onOpenChange: () => void;
}

export default function SettingsModal({
  isOpen,
  onOpenChange,
}: SettingsModalProps) {
  const dispatch = useAppDispatch();
  const features = useAppSelector(selectSettingsFeatures);
  const actorConfig = useAppSelector(selectActorConfig);
  const assistantConfig = useAppSelector(selectAssistantConfig);
  const settingsAnalytics = useAppSelector(selectSettingsAnalytics);
  const settingsDev = useAppSelector(selectSettingsDeveloper);
  const userRole = useAppSelector(selectUserRole);
  const models = getModels();

  let filteredFeatures: SettingsFeature[] = [];

  if (userRole !== 'admin' && userRole !== 'user_lets_talk') {
    features.forEach((feature) => {
      if (
        feature.key !== 'generate_lets_talk_response_befriender' &&
        feature.key !== 'generate_lets_talk_response_therapist'
      ) {
        filteredFeatures.push(feature);
      }
    });
  } else {
    filteredFeatures = features;
  }

  const selectedTypingSimulated = useMemo(
    () => actorConfig?.typingSimulated,
    [actorConfig]
  );

  const selectedAutomaticReplies = useMemo(
    () => actorConfig?.automaticReplies,
    [actorConfig]
  );

  const selectedActorDetails = useMemo(
    () => actorConfig?.actorDetails,
    [actorConfig]
  );

  const selectedActorProfile = useMemo(
    () => actorConfig?.actorProfile,
    [actorConfig]
  );

  const selectedActorModel = useMemo(
    () => new Set([actorConfig.modelTag]),
    [actorConfig]
  );
  const selectedAssistantModel = useMemo(
    () => new Set([assistantConfig.modelTag]),
    [assistantConfig]
  );
  const selectedProposeResponseModel = useMemo(
    () => new Set([assistantConfig.proposeResponseModelTag]),
    [assistantConfig]
  );

  const selectedActorModelLabel = useMemo(() => {
    for (const model of models) {
      if (model.tag === actorConfig.modelTag) {
        return model.label;
      }
    }
    return 'Unknown';
  }, [actorConfig, models]);

  const selectedAssistantModelLabel = useMemo(() => {
    for (const model of models) {
      if (model.tag === assistantConfig.modelTag) {
        return model.label;
      }
    }
    return 'Unknown';
  }, [assistantConfig, models]);

  const selectedProposeResponseModelLabel = useMemo(() => {
    for (const model of models) {
      if (model.tag === assistantConfig.proposeResponseModelTag) {
        return model.label;
      }
    }
    return 'Unknown';
  }, [assistantConfig, models]);

  const handleSelectedAssistantModelChange = (keys: Selection) => {
    if (keys === 'all') {
      return;
    }
    // keys only contains 1 ModelTag
    for (const key of keys) {
      // @ts-ignore
      dispatch(setAssistantModel(key));
    }
  };

  const handleSelectedProposeResponseModelChange = (keys: Selection) => {
    if (keys === 'all') {
      return;
    }
    // keys only contains 1 ModelTag
    for (const key of keys) {
      // @ts-ignore
      dispatch(setProposeResponseModel(key));
    }
  };

  const handleSelectedActorModelChange = (keys: Selection) => {
    if (keys === 'all') {
      return;
    }
    // keys only contains 1 ModelTag
    for (const key of keys) {
      // @ts-ignore
      dispatch(setActorModel(key));
    }
  };

  const handleToggleTypingSimulated = (isOn: boolean) => {
    dispatch(setTypingSimulated(isOn));
  };

  const handleToggleAutomaticReplies = (isOn: boolean) => {
    dispatch(setAutomaticReplies(isOn));
  };

  const handleToggleActorDetails = (isOn: boolean) => {
    dispatch(setActorDetails(isOn));
  };

  const handleToggleActorProfile = (isOn: boolean) => {
    dispatch(setActorProfile(isOn));
  };

  const handleToggleMaskClientMessage = (isOn: boolean) => {
    dispatch(setSettingsDeveloper({ ...settingsDev, maskClientMessage: isOn }));
  };

  const handleToggleIncludeExplanationState = (isOn: boolean) => {
    dispatch(
      setSettingsDeveloper({ ...settingsDev, includeExplanationState: isOn })
    );
  };

  const handleToggleMessageModelTag = (isShown: boolean) => {
    dispatch(
      setSettingsDeveloper({ ...settingsDev, showMessageModelTag: isShown })
    );
  };

  const handleToggleSelfPlayOptions = (isShown: boolean) => {
    dispatch(
      setSettingsDeveloper({ ...settingsDev, showSelfPlayOptions: isShown })
    );
  };

  const handleToggleTrafficLight = (isShown: boolean) => {
    dispatch(
      setSettingsAnalytics({ ...settingsAnalytics, showTrafficLight: isShown })
    );
  };

  const handleToggleAutoTrafficLight = (isShown: boolean) => {
    dispatch(
      setSettingsAnalytics({ ...settingsAnalytics, autoTrafficLight: isShown })
    );
  };

  const handleToggleReasoning = (isShown: boolean) => {
    dispatch(
      setSettingsAnalytics({ ...settingsAnalytics, reasoning: isShown })
    );
  };

  const handleToggleAutoSentimentEvaluation = (isAuto: boolean) => {
    dispatch(
      setSettingsAnalytics({
        ...settingsAnalytics,
        autoEvaluateSentiment: isAuto,
      })
    );
  };

  const handleToggleSuicidalityEvaluation = (isAuto: boolean) => {
    dispatch(
      setSettingsAnalytics({
        ...settingsAnalytics,
        evaluateSuicidality: isAuto,
      })
    );
  };

  const handleResetEverything = () => {
    localStorage.clear();
    window.location.reload();
  };

  const handleLogout = () => {
    dispatch(logout());
  };

  const modelSelections =
    process.env.REACT_APP_DEPLOYMENT_TYPE === 'SOS'
      ? [
          { tag: 'gpt-5.2', label: 'GPT-5.2' },
          { tag: 'gpt-5.2-enabled', label: 'GPT-5.2 Enabled Reasoning' },
        ]
      : models;

  const assistantModels = (
    <>
      <SettingsRow header="Assistant LLM Model">
        <Dropdown>
          <DropdownTrigger>
            <Button variant="flat" endContent={<Icon icon={FaAngleDown} />}>
              {selectedAssistantModelLabel}
            </Button>
          </DropdownTrigger>
          <DropdownMenu
            aria-label="Assistant model dropdown"
            variant="flat"
            selectionMode="single"
            selectedKeys={selectedAssistantModel}
            onSelectionChange={handleSelectedAssistantModelChange}
            className={`${process.env.REACT_APP_DEPLOYMENT_TYPE === 'SOS' ? 'sos-settings-dropdown' : 'settings-dropdown'}`}
          >
            {modelSelections.map((m) => (
              <DropdownItem key={m.tag}>{m.label}</DropdownItem>
            ))}
          </DropdownMenu>
        </Dropdown>
      </SettingsRow>
      <SettingsRow header="Propose Response LLM Model">
        <Dropdown>
          <DropdownTrigger>
            <Button variant="flat" endContent={<Icon icon={FaAngleDown} />}>
              {selectedProposeResponseModelLabel}
            </Button>
          </DropdownTrigger>
          <DropdownMenu
            aria-label="Propose Response model dropdown"
            variant="flat"
            selectionMode="single"
            selectedKeys={selectedProposeResponseModel}
            onSelectionChange={handleSelectedProposeResponseModelChange}
            className={`${process.env.REACT_APP_DEPLOYMENT_TYPE === 'SOS' ? 'sos-settings-dropdown' : 'settings-dropdown'}`}
          >
            {modelSelections.map((m) => (
              <DropdownItem key={m.tag}>{m.label}</DropdownItem>
            ))}
          </DropdownMenu>
        </Dropdown>
      </SettingsRow>
    </>
  );

  const actorModels =
    process.env.REACT_APP_DEPLOYMENT_TYPE === 'SOS'
      ? [
          {
            tag: 'claude-4.5-sonnet',
            label: 'Claude 4.5 Sonnet',
          },
        ]
      : models;

  const actorOptions = (
    <>
      <SettingsRow header="Actor LLM Model">
        <Dropdown>
          <DropdownTrigger>
            <Button variant="flat" endContent={<Icon icon={FaAngleDown} />}>
              {selectedActorModelLabel}
            </Button>
          </DropdownTrigger>
          <DropdownMenu
            aria-label="Actor model dropdown"
            variant="flat"
            selectionMode="single"
            selectedKeys={selectedActorModel}
            onSelectionChange={handleSelectedActorModelChange}
            className={`${process.env.REACT_APP_DEPLOYMENT_TYPE === 'SOS' ? 'sos-settings-dropdown' : 'settings-dropdown'}`}
          >
            {actorModels.map((m) => (
              <DropdownItem key={m.tag}>{m.label}</DropdownItem>
            ))}
          </DropdownMenu>
        </Dropdown>
      </SettingsRow>
      <SettingsRow
        header="Simulated Typing"
        description="Adds a delay between messages to simulate typing"
      >
        <Switch
          aria-label="is on"
          isSelected={selectedTypingSimulated}
          onValueChange={handleToggleTypingSimulated}
        />
      </SettingsRow>
      <SettingsRow
        header="Automatic Replies"
        description="Actor replies automatically when a message is sent"
      >
        <Switch
          aria-label="is on"
          isSelected={selectedAutomaticReplies}
          onValueChange={handleToggleAutomaticReplies}
        />
      </SettingsRow>
      <SettingsRow
        header="Show Actor Concerns"
        description="Show actor concerns and difficulty level in the Actor List"
      >
        <Switch
          aria-label="is on"
          isSelected={selectedActorDetails}
          onValueChange={handleToggleActorDetails}
        />
      </SettingsRow>
      <SettingsRow
        header="Start with Actor Profile"
        description="Start actor conversations with mini-survey profile"
      >
        <Switch
          aria-label="is on"
          isSelected={selectedActorProfile}
          onValueChange={handleToggleActorProfile}
        />
      </SettingsRow>
    </>
  );

  const analyticsOptions = (
    <>
      <SettingsRow
        header="Enable Traffic Light (Suicide Risk Alerts)"
        description="Show Traffic Light button in client and actor chats"
        disabled={
          userRole === 'user_trainee' ||
          process.env.REACT_APP_DEPLOYMENT_TYPE === 'GENERIC'
        }
      >
        <Switch
          aria-label="is shown"
          isSelected={settingsAnalytics.showTrafficLight}
          onValueChange={handleToggleTrafficLight}
          className={`${process.env.REACT_APP_DEPLOYMENT_TYPE === 'SOS' ? 'traffic-light-toggle' : process.env.REACT_APP_DEPLOYMENT_TYPE === 'GENERIC' ? 'disable-traffic-light-toggle' : userRole === 'user_trainee' ? 'disable-traffic-light-toggle' : 'traffic-light-toggle'}`}
        />
      </SettingsRow>
      <SettingsRow
        header="Auto-run Traffic Light"
        description="Run Traffic Light automatically after each incoming client message"
        disabled={
          userRole === 'user_trainee' ||
          userRole === 'user_sos' ||
          process.env.REACT_APP_DEPLOYMENT_TYPE === 'GENERIC'
        }
      >
        <Switch
          aria-label="is shown"
          isSelected={settingsAnalytics.autoTrafficLight}
          onValueChange={handleToggleAutoTrafficLight}
          className={`${process.env.REACT_APP_DEPLOYMENT_TYPE === 'GENERIC' ? 'disable-auto-traffic-light-toggle' : userRole === 'user_trainee' || userRole === 'user_sos' ? 'disable-auto-traffic-light-toggle' : 'auto-traffic-light-toggle'}`}
        />
      </SettingsRow>
      <SettingsRow
        header="Reasoning"
        description="Use more accurate (but slower) model and show model reasoning"
        disabled={
          userRole === 'user_trainee' ||
          process.env.REACT_APP_DEPLOYMENT_TYPE === 'GENERIC'
        }
      >
        <Switch
          aria-label="is auto"
          isSelected={settingsAnalytics.reasoning}
          onValueChange={handleToggleReasoning}
          className={`${process.env.REACT_APP_DEPLOYMENT_TYPE === 'SOS' ? 'reasoning-toggle' : process.env.REACT_APP_DEPLOYMENT_TYPE === 'GENERIC' ? 'disable-reasoning-toggle' : userRole === 'user_trainee' ? 'disable-reasoning-toggle' : 'reasoning-toggle'}`}
        />
      </SettingsRow>
      {process.env.REACT_APP_DEPLOYMENT_TYPE === 'GENERIC' && (
        <>
          <SettingsRow
            header="Auto sentiment evaluation of incoming messages"
            description={
              'Whether to automatically evaluate ' +
              'the sentiment of messages from affected persons'
            }
            disabled={userRole === 'user_trainee'}
          >
            <Switch
              aria-label="is auto"
              isSelected={settingsAnalytics.autoEvaluateSentiment}
              onValueChange={handleToggleAutoSentimentEvaluation}
              className={`${userRole === 'user_trainee' ? 'disable-sentiment-toggle' : 'sentiment-toggle'}`}
            />
          </SettingsRow>
          <SettingsRow
            header="Auto suicidality evaluation of incoming messages"
            description={
              'Whether to automatically evaluate the suicidality of messages from affected persons'
            }
            disabled={userRole === 'user_trainee'}
          >
            <Switch
              aria-label="is auto"
              isSelected={settingsAnalytics.evaluateSuicidality}
              onValueChange={handleToggleSuicidalityEvaluation}
              className={`${userRole === 'user_trainee' ? 'disable-suicidality-toggle' : 'suicidality-toggle'}`}
            />
          </SettingsRow>
        </>
      )}
    </>
  );

  const devOptions = (
    <>
      <SettingsRow
        header="Include Explanation and State"
        description="Include explanation and state in Propose Response for trained model"
        disabled={process.env.REACT_APP_DEPLOYMENT_TYPE === 'GENERIC'}
      >
        <Switch
          aria-label="is on"
          isSelected={settingsDev.includeExplanationState}
          onValueChange={handleToggleIncludeExplanationState}
          className={`${process.env.REACT_APP_DEPLOYMENT_TYPE === 'SOS' ? 'explanation-toggle' : 'disable-explanation-toggle'}`}
        />
      </SettingsRow>

      <SettingsRow
        header="Mask Client Message"
        description="Mask the client message (remove PII) before sending API query to LLM"
        disabled={process.env.REACT_APP_DEPLOYMENT_TYPE === 'GENERIC'}
      >
        <Switch
          aria-label="is on"
          isSelected={settingsDev.maskClientMessage}
          onValueChange={handleToggleMaskClientMessage}
          className={`${process.env.REACT_APP_DEPLOYMENT_TYPE === 'SOS' ? 'masking-toggle' : 'disable-masking-toggle'}`}
        />
      </SettingsRow>

      <SettingsRow
        header="Message Model Tag"
        description="Whether to show model tag for assistant messages"
      >
        <Switch
          aria-label="is shown"
          isSelected={settingsDev.showMessageModelTag}
          onValueChange={handleToggleMessageModelTag}
        />
      </SettingsRow>
      <SettingsRow
        header="Enable Self-play with Actors"
        description="Whether to show self-play options in chats with actors"
      >
        <Switch
          aria-label="is shown"
          isSelected={settingsDev.showSelfPlayOptions}
          onValueChange={handleToggleSelfPlayOptions}
        />
      </SettingsRow>
      <SettingsRow
        header="Reset Everything"
        description={
          'Deletes ALL settings, and other local data ' +
          '(usually only for debugging purposes)'
        }
      >
        <Button color="danger" variant="flat" onPress={handleResetEverything}>
          Reset
        </Button>
      </SettingsRow>
    </>
  );

  const name =
    process.env.REACT_APP_DEPLOYMENT_TYPE === 'GENERIC'
      ? GENERIC_APP.name
      : SOS_APP.name;

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      size="2xl"
      backdrop="blur"
      scrollBehavior="inside"
    >
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader>Settings</ModalHeader>
            <ModalBody className="border-y-1">
              <SettingsSection header={`${name} Assistant functionalities`}>
                <p className="text-xs italic">
                  Features with an "A" toggle can be automatically run after
                  each client or actor message.
                </p>
                {filteredFeatures.map((feat) => (
                  <SettingsToggle key={feat.key} feature={feat} />
                ))}
                <Divider />
                <PromptButton />
              </SettingsSection>
              <SettingsSection header="LLM model selection">
                {assistantModels}
              </SettingsSection>
              <SettingsSection header="Actor options">
                {actorOptions}
              </SettingsSection>
              {userRole !== 'user_scs' && (
                <SettingsSection header="Analytics options">
                  {analyticsOptions}
                </SettingsSection>
              )}
              {userRole !== 'user_trainee' && (
                <SettingsSection header="Developer options">
                  {devOptions}
                </SettingsSection>
              )}
            </ModalBody>
            <ModalFooter className="justify-between">
              <ButtonWithConfirmModal
                onPress={handleLogout}
                buttonContent="Logout"
                confirmLabel="Logout"
                modalHeader="Logout?"
              />
              <Button color="default" onPress={onClose}>
                Done
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}

function SettingsSection({
  header,
  children,
}: { header: string } & PropsWithChildren) {
  return (
    <>
      <p className="text-sm font-semibold">{header}</p>
      <Divider />
      {children}
      <Spacer y={4} />
    </>
  );
}
